import AdapterDateFns from "@mui/lab/AdapterDateFns";
import LocalizationProvider from "@mui/lab/LocalizationProvider";
import MobileTimePicker from "@mui/lab/MobileTimePicker";
import Grid from "@mui/material/Grid";
import TextField from "@mui/material/TextField";
import { Controller } from "react-hook-form";
import FieldError from "shared/organisms/dynamicform/FieldError";
import { snackbarRequest } from "shared/molecules/snackbar/snackbar-slice";
import { useAppDispatch } from "hooks/redux";
import { parse, format } from "date-fns";

interface IDatePickerFieldProps {
  field: any;
  control: any;
  errors: any;
  getValues: any;
}

const TimePickerField = ({
  field,
  control,
  errors,
  getValues,
}: IDatePickerFieldProps) => {
  const dispatch: any = useAppDispatch();
  const values: any = getValues();

  // Parse time value from 24-hour format string to Date object
  const parseTimeValue = (timeString: string | null) => {
    if (!timeString) return null;
    try {
      // Handle different time string formats
      if (timeString.includes("+")) {
        // Example input: "09:00:00+05:30"
        return parse(timeString, "HH:mm:ssXXX", new Date());
      } else if (timeString.includes(":") && timeString.split(":").length === 3) {
        // Example input: "09:00:00"
        return parse(timeString, "HH:mm:ss", new Date());
      } else if (timeString.includes(":") && timeString.split(":").length === 2) {
        // Example input: "09:00"
        return parse(timeString, "HH:mm", new Date());
      }
      return null;
    } catch (error) {
      console.error("Error parsing time:", error);
      return null;
    }
  };

  // Format Date object back to 24-hour format string for storage
  const formatTimeForStorage = (date: Date | null) => {
    if (!date) return null;
    return format(date, "HH:mm:ss");
  };

  const handleChange = (selectedDate: Date | null, onChange: (value: any) => void) => {
    if (field.minTime && selectedDate) {
      const minTime = parseTimeValue(values[field.minTime]);
      if (minTime) {
        const minTimeValue = minTime.getHours() * 60 + minTime.getMinutes();
        const selectedTimeValue = selectedDate.getHours() * 60 + selectedDate.getMinutes();
        
        if (selectedTimeValue < minTimeValue) {
          dispatch(
            snackbarRequest({
              type: "error",
              message: `Selected time should be greater than ${field.minTime}`,
            })
          );
          return;
        }
      }
    }

    if (field.maxTime && selectedDate) {
      const maxTime = parseTimeValue(values[field.maxTime]);
      if (maxTime) {
        const maxTimeValue = maxTime.getHours() * 60 + maxTime.getMinutes();
        const selectedTimeValue = selectedDate.getHours() * 60 + selectedDate.getMinutes();
        
        if (selectedTimeValue > maxTimeValue) {
          dispatch(
            snackbarRequest({
              type: "error",
              message: `Selected time should be less than ${field.maxTime}`,
            })
          );
          return;
        }
      }
    }

    // Convert to 24-hour format for storage
    const formattedTime = formatTimeForStorage(selectedDate);
    onChange(formattedTime);
  };

  return (
    <Grid item {...field.breakpoints}>
      <Controller
        name={field.id}
        control={control}
        render={({ field: { onChange, value } }) => (
          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <MobileTimePicker
              renderInput={(params) => (
                <TextField
                  {...params}
                  sx={{ width: "100%" }}
                  error={errors && errors[field.id]?.message}
                  required={field.required}
                  size="small"
                  placeholder="Select time"
                />
              )}
              inputFormat="hh:mm a" // 12-hour format with AM/PM
              label={field.label}
              value={parseTimeValue(value)} // Convert stored 24-hour format to Date object for display
              onChange={(selectedDate) => handleChange(selectedDate, onChange)}
              minTime={values[field.minTime] ? parseTimeValue(values[field.minTime]) : undefined}
              maxTime={values[field.maxTime] ? parseTimeValue(values[field.maxTime]) : undefined}
              disabled={field.disabled}
              ampm={true} // Enable AM/PM selector
            />
          </LocalizationProvider>
        )}
      />
      {errors && errors[field.id]?.message && (
        <FieldError field={field} errors={errors} />
      )}
    </Grid>
  );
};

export default TimePickerField;